import React, { useEffect, useState, useMemo } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { db, storage } from '../../firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import './ManajemenBahan.css';

const initialForm = {
  nama: '', 
  kode: '', 
  stok: '', 
  satuan: '', 
  jenis: '', // 'cair' or 'serbuk'
  expiry: '', 
  gambarUrl: '', 
  dokumenUrl: '',
  keterangan: '' // Added for additional info
};

const ManajemenBahan = () => {
  const [bahan, setBahan] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [gambarFile, setGambarFile] = useState(null);
  const [dokumenFile, setDokumenFile] = useState(null);

  // Handle form input
  const handleChange = e => {
    const { name, value } = e.target;
    if (name === 'jenis') {
      // Set default satuan based on jenis
      let newSatuan = '';
      if (value === 'cair') newSatuan = 'ml';
      else if (value === 'serbuk') newSatuan = 'mg';
      setForm({ ...form, jenis: value, satuan: newSatuan });
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  // Fetch data
  const fetchBahan = async () => {
    setLoading(true);
    try {
      const querySnapshot = await getDocs(collection(db, 'bahan'));
      const data = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setBahan(data);
    } catch (err) {
      setError('Gagal mengambil data bahan');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBahan(); }, []);

  // Handle file input
  const handleGambarChange = e => {
    setGambarFile(e.target.files[0]);
  };
  const handleDokumenChange = e => {
    setDokumenFile(e.target.files[0]);
  };

  // Upload file helper
  const uploadFile = async (file, folder) => {
    if (!file) return '';
    const fileRef = ref(storage, `${folder}/${Date.now()}_${file.name}`);
    await uploadBytes(fileRef, file);
    return await getDownloadURL(fileRef);
  };

  // Delete file helper
  const deleteFileByUrl = async url => {
    if (!url) return;
    try {
      const fileRef = ref(storage, url);
      await deleteObject(fileRef);
      console.log("Successfully deleted file:", url);
    } catch (error) {
      console.error("Error deleting file from URL:", url, error);
    }
  };

  // Add or update
  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    setError('');
    let success = false;

    try {
      let gambarUrl = form.gambarUrl;
      let dokumenUrl = form.dokumenUrl;

      if (gambarFile) {
        if (gambarUrl) await deleteFileByUrl(gambarUrl);
        gambarUrl = await uploadFile(gambarFile, 'bahan_gambar');
      }
      if (dokumenFile) {
        if (dokumenUrl) await deleteFileByUrl(dokumenUrl);
        dokumenUrl = await uploadFile(dokumenFile, 'bahan_dokumen');
      }

      const dataToSave = {
        ...form,
        stok: Number(form.stok) || 0,
        gambarUrl,
        dokumenUrl
      };

      if (editId) {
        await updateDoc(doc(db, 'bahan', editId), dataToSave);
        console.log("Material updated successfully:", editId);
      } else {
        const docRef = await addDoc(collection(db, 'bahan'), dataToSave);
        console.log("Material added successfully:", docRef.id);
      }

      success = true;

    } catch (err) {
      console.error("Error saving material:", err);
      setError(`Gagal menyimpan data: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
      if (success) {
        setForm(initialForm);
        setEditId(null);
        setGambarFile(null);
        setDokumenFile(null);
        fetchBahan();
      }
    }
  };

  // Edit
  const handleEdit = item => {
    setForm({
      nama: item.nama || '',
      kode: item.kode || '',
      stok: item.stok || '',
      satuan: item.satuan || '',
      jenis: item.jenis || '',
      expiry: item.expiry || '',
      gambarUrl: item.gambarUrl || '',
      dokumenUrl: item.dokumenUrl || '',
      keterangan: item.keterangan || ''
    });
    setEditId(item.id);
    setGambarFile(null);
    setDokumenFile(null);
  };

  // Delete
  const handleDelete = async id => {
    if (!window.confirm('Hapus bahan ini?')) return;
    setLoading(true);
    setError('');
    try {
      const item = bahan.find(x => x.id === id);
      if (item?.gambarUrl) await deleteFileByUrl(item.gambarUrl);
      if (item?.dokumenUrl) await deleteFileByUrl(item.dokumenUrl);
      await deleteDoc(doc(db, 'bahan', id));
      fetchBahan();
    } catch (err) {
      console.error("Error deleting material:", err);
      setError(`Gagal menghapus data: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  // Add state for search term
  const [searchTerm, setSearchTerm] = useState('');
  
  // Filtered list based on search term
  const filteredBahan = useMemo(() => {
    if (!searchTerm) {
      return bahan;
    }
    return bahan.filter(item =>
      item.nama.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.kode && item.kode.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  }, [bahan, searchTerm]);

  // Add states for popup management
  const [showAddPopup, setShowAddPopup] = useState(false);
  const [showEditPopup, setShowEditPopup] = useState(false);

  // Modified handleEdit to show popup
  const handleEditClick = item => {
    handleEdit(item);
    setShowEditPopup(true);
  };

  // Handler for "Tambah Item" button
  const handleAddClick = () => {
    setForm(initialForm);
    setEditId(null);
    setError('');
    setGambarFile(null);
    setDokumenFile(null);
    setShowAddPopup(true);
  };

  // Close popup handler
  const handleClosePopup = () => {
    if (editId) {
      setForm(initialForm);
      setEditId(null);
      setError('');
      setGambarFile(null);
      setDokumenFile(null);
    }
    setShowAddPopup(false);
    setShowEditPopup(false);
  };

  return (
    <div className="manajemen-bahan-container">
      <div className="header-section">
        <h2>Manajemen Bahan</h2>
        <button 
          onClick={handleAddClick} 
          className="btn btn-primary btn-add-bahan"
        >
          Tambah Bahan
        </button>
      </div>

      {/* Display Error and Loading */}
      {error && <div className="error-message">{error}</div>}
      {loading && !editId && <div className="loading-message">Memuat data...</div>}

      {/* Simple Search Input */}
      <div className="search-container">
        <input
          type="text"
          placeholder="Cari bahan berdasarkan nama atau kode..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>

      {/* Add/Edit Popup */}
      {(showAddPopup || showEditPopup) && (
        <div 
          className="popup-overlay" 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}
        >
          <div 
            className="popup-content"
            style={{
              backgroundColor: 'white',
              padding: '20px',
              borderRadius: '5px',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <h3>{editId ? 'Edit Bahan' : 'Tambah Bahan Baru'}</h3>
              <button 
                onClick={handleClosePopup}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '20px',
                  cursor: 'pointer'
                }}
              >
                ×
              </button>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <input name="nama" placeholder="Nama Bahan" value={form.nama} onChange={handleChange} required className="form-input" />
                <input name="kode" placeholder="Kode Unik" value={form.kode} onChange={handleChange} required className="form-input" />
              </div>
              <div className="form-row">
                <input name="stok" placeholder="Stok" type="number" value={form.stok} onChange={handleChange} required className="form-input" />
                <select name="jenis" value={form.jenis} onChange={handleChange} required className="form-input">
                  <option value="">-- Pilih Jenis --</option>
                  <option value="cair">Cairan (ml)</option>
                  <option value="serbuk">Serbuk (mg/gram)</option>
                  <option value="lainnya">Lainnya</option>
                </select>
                <input 
                  name="satuan" 
                  placeholder="Satuan" 
                  value={form.satuan} 
                  onChange={handleChange} 
                  required 
                  className="form-input"
                  readOnly={form.jenis === 'cair' || form.jenis === 'serbuk'}
                />
                <input name="expiry" placeholder="Tanggal Kadaluarsa" type="date" value={form.expiry} onChange={handleChange} className="form-input" />
              </div>
              <div className="form-row">
                <textarea 
                  name="keterangan" 
                  placeholder="Keterangan tambahan" 
                  value={form.keterangan} 
                  onChange={handleChange} 
                  className="form-input"
                  style={{ width: '100%', minHeight: '60px' }}
                />
              </div>
              <div className="form-row file-inputs">
                <label className="file-label">
                  Gambar:
                  <input type="file" accept="image/*" onChange={handleGambarChange} className="file-input" />
                  {gambarFile && <span className="file-name">{gambarFile.name}</span>}
                </label>
                <label className="file-label">
                  Dokumen (MSDS/etc):
                  <input type="file" accept="application/pdf,application/msword,.doc,.docx" onChange={handleDokumenChange} className="file-input" />
                  {dokumenFile && <span className="file-name">{dokumenFile.name}</span>}
                </label>
              </div>
              {editId && (
                <div className="current-files">
                  {form.gambarUrl && (
                    <div className="current-file-item">
                      <span>Gambar Saat Ini: </span>
                      <img src={form.gambarUrl} alt="Current Bahan" style={{ maxWidth: '100px', maxHeight: '60px', verticalAlign: 'middle' }} />
                    </div>
                  )}
                  {form.dokumenUrl && (
                    <div className="current-file-item">
                      <span>Dokumen Saat Ini: </span>
                      <a href={form.dokumenUrl} target="_blank" rel="noopener noreferrer">Lihat Dokumen</a>
                    </div>
                  )}
                </div>
              )}
              <div className="form-actions" style={{ marginTop: '15px' }}>
                <button type="submit" disabled={loading} className="btn btn-primary">
                  {loading ? 'Menyimpan...' : (editId ? 'Update Bahan' : 'Tambah Bahan')}
                </button>
                <button
                  type="button"
                  onClick={handleClosePopup}
                  disabled={loading}
                  className="btn btn-secondary"
                  style={{ marginLeft: '10px' }}
                >
                  Batal
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Table Section */}
      <div className="bahan-table-container">
        <table className="bahan-table">
          <thead>
            <tr>
              <th>Nama</th>
              <th>Kode</th>
              <th>Jenis</th>
              <th>Stok</th>
              <th>Satuan</th>
              <th>Expiry</th>
              <th>Keterangan</th>
              <th>Gambar</th>
              <th>Aksi</th>
            </tr>
          </thead>
          <tbody>
            {filteredBahan.map(item => (
              <tr key={item.id}>
                <td>{item.nama}</td>
                <td>{item.kode}</td>
                <td>{item.jenis || 'N/A'}</td>
                <td>{item.stok}</td>
                <td>{item.satuan}</td>
                <td>{item.expiry}</td>
                <td>{item.keterangan || '-'}</td>
                <td>
                  {item.gambarUrl ? <img src={item.gambarUrl} alt={item.nama} className="table-image" /> : 'N/A'}
                </td>
                <td className="action-buttons">
                  <button onClick={() => handleEditClick(item)} className="btn btn-edit" disabled={loading}>Edit</button>
                  <button onClick={() => handleDelete(item.id)} className="btn btn-delete" disabled={loading}>Hapus</button>
                </td>
              </tr>
            ))}
            {filteredBahan.length === 0 && !loading && (
              <tr>
                <td colSpan="9" style={{ textAlign: 'center' }}>
                  {searchTerm ? 'Tidak ada bahan yang cocok dengan pencarian.' : 'Belum ada data bahan.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ManajemenBahan;
